import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Alert } from '@mui/material';
import type { Dependent } from '../../employeeTypes';
import { getDependentColumns } from '../../config/dependentColumns';
import { TableTemplate } from '../../../../components/ui/tableTemplate/TableTemplate';
import theme from '../../../../theme/theme';

type Props = {
  dependents: Dependent[];
  t: TFunction;
  /** Optional toolbar node forwarded to TableTemplate (title + icon badge). */
  toolbar?: ReactNode;
  /** Fold the table behind a header chevron toggle. */
  collapsible?: boolean;
  /** Initial expanded state when {@link collapsible}. Defaults to expanded. */
  defaultExpanded?: boolean;
};

/**
 * Table for displaying a list of dependents, built on TableTemplate.
 * Column definitions live in config/dependentColumns.tsx.
 *
 * getDependentColumns(t) is called here — not at module level — so `t` can
 * close over the boolean cell renders (TableColumn.render receives only the row).
 */
export function DependentsTable({ dependents, t, toolbar, collapsible, defaultExpanded }: Props) {
  const hasDependents = dependents.length > 0;

  return (
    <TableTemplate<Dependent>
      columns={getDependentColumns(t)}
      rows={dependents}
      toolbar={toolbar}
      collapsible={collapsible}
      defaultExpanded={defaultExpanded}
      maxHeight={hasDependents ? theme.detailPage.tableMaxHeight : undefined}
      hideHeader={!hasDependents}
      errorState={
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mt: 1, fontSize: theme.typography.body2.fontSize }}
        >
          {t('employeeDetail.sections.noDependents')}
        </Alert>
      }
    />
  );
}
